/**
 * TAILWIND TABLE KOMPONENTE
 *
 * Flexible Tabellen-Komponente mit Dark/Light Mode, Brand-Gold Hover und Context-basiertem Styling.
 *
 * Exportiert 6 Komponenten:
 *
 * --- TWTable (Wrapper) ---
 *   striped?    - boolean (default: false), abwechselnde Zeilenfarben
 *   dense?      - boolean (default: false), kompakteres Padding (py-2 statt py-4)
 *   grid?       - boolean (default: false), vertikale Trennlinien zwischen Spalten
 *   hover?      - boolean (default: true), Hover-Effekt auf Zeilen
 *   hoverStyle? - default (Gold) | subtle (Grau) | clickable (Gold + Cursor)
 *
 * --- TWTableHead ---  (thead, automatisch grauer Hintergrund)
 * --- TWTableBody ---  (tbody)
 * --- TWTableRow ---
 *   disableHover? - boolean, Hover für diese Zeile deaktivieren
 *   onClick?      - () => void, macht Zeile klickbar (automatisch hoverStyle=clickable)
 * --- TWTableHeader --- (th, uppercase, font-semibold, text-xs)
 * --- TWTableCell ---
 *   noBorder? - boolean, Trennlinie unter der Zelle entfernen
 *
 * Usage:
 *   <TWTable striped hover>
 *     <TWTableHead>
 *       <TWTableRow><TWTableHeader>Name</TWTableHeader></TWTableRow>
 *     </TWTableHead>
 *     <TWTableBody>
 *       <TWTableRow><TWTableCell>Wert</TWTableCell></TWTableRow>
 *     </TWTableBody>
 *   </TWTable>
 */

import clsx from 'clsx';
import React, { createContext, useContext } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

// ============================================
// DESIGN TOKENS (Zentrale Stelle für Anpassungen)
// ============================================
const tableStyles = {
  // Basis-Farben (Tailwind-Plus Pattern: Header kraeftig, Zellen gestuft)
  text: {
    header: 'text-gray-900 dark:text-white',
    cell: 'text-gray-500 dark:text-gray-400',
  },

  // Hover-Effekte (sehr dezent, kein Gold-Flash)
  hover: {
    row: 'hover:bg-gray-50 dark:hover:bg-white/[0.03]',
    rowSubtle: 'hover:bg-gray-50 dark:hover:bg-white/[0.03]',
    rowClickable: 'hover:bg-gray-50 dark:hover:bg-white/[0.03] cursor-pointer',
  },

  // Borders (Tailwind-Plus: divide-y statt Row-Borders)
  border: {
    table: 'divide-y divide-gray-300 dark:divide-white/15',
    tbody: 'divide-y divide-gray-200 dark:divide-white/10',
    grid: 'border-l border-gray-200 dark:border-white/10 first:border-l-0',
  },

  // Spacing (Tailwind-Plus: py-3.5 im Header, py-4 in Zellen)
  padding: {
    header: 'px-3 py-3.5',
    cell: 'px-3 py-4',
    cellDense: 'px-3 py-2',
  },

  // Transitions
  transition: 'transition-colors duration-150',
};

// ============================================
// CONTEXT
// ============================================
interface TableContextValue {
  striped: boolean;
  dense: boolean;
  grid: boolean;
  hover: boolean;
  hoverStyle: 'default' | 'subtle' | 'clickable';
}

const TableContext = createContext<TableContextValue>({
  striped: false,
  dense: false,
  grid: false,
  hover: true,
  hoverStyle: 'default',
});

// ============================================
// TABLE KOMPONENTE
// ============================================
interface TWTableProps extends React.ComponentPropsWithoutRef<'div'> {
  /** Abwechselnde Zeilenfarben */
  striped?: boolean;
  /** Kompaktere Darstellung */
  dense?: boolean;
  /** Vertikale Trennlinien */
  grid?: boolean;
  /** Hover-Effekt auf Zeilen */
  hover?: boolean;
  /** Art des Hover-Effekts */
  hoverStyle?: 'default' | 'subtle' | 'clickable';
  children: React.ReactNode;
}

export function TWTable({
  striped = false,
  dense = false,
  grid = false,
  hover = true,
  hoverStyle = 'default',
  className,
  children,
  ...props
}: TWTableProps) {
  return (
    <TableContext.Provider value={{ striped, dense, grid, hover, hoverStyle }}>
      <div className="flow-root">
        <div {...props} className={clsx(className, 'overflow-x-auto')}>
          <div className="inline-block min-w-full align-middle">
            <table className={clsx('min-w-full text-left text-sm', tableStyles.border.table)}>
              {children}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

// ============================================
// TABLE HEAD
// ============================================
export function TWTableHead({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'thead'>) {
  return (
    <thead
      {...props}
      className={clsx(
        className,
        tableStyles.text.header,
        'bg-gray-50 dark:bg-gray-800/75',
        '[&_tr]:!bg-inherit [&_tr]:hover:!bg-inherit'
      )}
    >
      {children}
    </thead>
  );
}

// ============================================
// TABLE BODY
// ============================================
export function TWTableBody({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'tbody'>) {
  return <tbody {...props} className={clsx(className, tableStyles.border.tbody)} />;
}

// ============================================
// TABLE ROW
// ============================================
interface TWTableRowProps extends React.ComponentPropsWithoutRef<'tr'> {
  /** Override hover für diese spezifische Zeile */
  disableHover?: boolean;
  /** Klick-Handler (macht Zeile klickbar) */
  onClick?: () => void;
}

export function TWTableRow({
  className,
  disableHover = false,
  onClick,
  children,
  ...props
}: TWTableRowProps) {
  const { striped, hover, hoverStyle } = useContext(TableContext);

  // Hover-Klasse bestimmen
  const getHoverClass = () => {
    if (disableHover || !hover) return '';

    if (onClick) return tableStyles.hover.rowClickable;

    switch (hoverStyle) {
      case 'subtle':
        return tableStyles.hover.rowSubtle;
      case 'clickable':
        return tableStyles.hover.rowClickable;
      default:
        return tableStyles.hover.row;
    }
  };

  return (
    <tr
      {...props}
      onClick={onClick}
      className={clsx(
        className,
        tableStyles.transition,
        // Striped Background (sehr dezent)
        striped && 'even:bg-gray-50/60 dark:even:bg-white/[0.02]',
        // Hover Effect
        getHoverClass()
      )}
    >
      {children}
    </tr>
  );
}

// ============================================
// TABLE HEADER (TH)
// ============================================
type SortDirection = 'asc' | 'desc' | null;

interface TWTableHeaderProps extends React.ComponentPropsWithoutRef<'th'> {
  /** Macht den Header als Sortier-Trigger klickbar */
  sortable?: boolean;
  /** Aktive Sortier-Richtung fuer diesen Header ('asc' | 'desc' | null/undefined) */
  sortDirection?: SortDirection;
  /** Klick-Handler fuer Sortierung (typischerweise toggelt asc/desc) */
  onSort?: () => void;
}

export function TWTableHeader({
  className,
  children,
  sortable = false,
  sortDirection = null,
  onSort,
  ...props
}: TWTableHeaderProps) {
  const { grid } = useContext(TableContext);

  return (
    <th
      scope="col"
      {...props}
      className={clsx(
        className,
        tableStyles.padding.header,
        'text-sm font-semibold',
        grid && tableStyles.border.grid
      )}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className="group inline-flex items-center gap-2 font-semibold text-inherit hover:text-brand dark:hover:text-brand focus:outline-none"
        >
          {children}
          <span
            className={clsx(
              'flex-none rounded-sm',
              sortDirection
                ? 'bg-gray-100 text-gray-900 group-hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:group-hover:bg-gray-700'
                : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300'
            )}
          >
            {sortDirection === 'asc' ? (
              <ChevronUp aria-hidden="true" className="size-4" />
            ) : sortDirection === 'desc' ? (
              <ChevronDown aria-hidden="true" className="size-4" />
            ) : (
              <ChevronsUpDown aria-hidden="true" className="size-4" />
            )}
          </span>
        </button>
      ) : (
        children
      )}
    </th>
  );
}

// ============================================
// TABLE CELL (TD)
// ============================================
interface TWTableCellProps extends React.ComponentPropsWithoutRef<'td'> {
  /** Zelle ohne Border */
  noBorder?: boolean;
}

export function TWTableCell({
  className,
  children,
  ...props
}: TWTableCellProps) {
  const { dense, grid } = useContext(TableContext);

  return (
    <td
      {...props}
      className={clsx(
        className,
        dense ? tableStyles.padding.cellDense : tableStyles.padding.cell,
        'whitespace-nowrap',
        tableStyles.text.cell,
        grid && tableStyles.border.grid
      )}
    >
      {children}
    </td>
  );
}

// ============================================
// BEISPIEL-VERWENDUNG (als Kommentar)
// ============================================
/*
// Standard-Tabelle mit Hover
<TWTable hover>
  <TWTableHead>
    <TWTableRow>
      <TWTableHeader>Asset</TWTableHeader>
      <TWTableHeader>Preis</TWTableHeader>
    </TWTableRow>
  </TWTableHead>
  <TWTableBody>
    <TWTableRow>
      <TWTableCell>Bitcoin</TWTableCell>
      <TWTableCell>$67,000</TWTableCell>
    </TWTableRow>
  </TWTableBody>
</TWTable>

// Gestreifte, kompakte Tabelle mit klickbaren Zeilen
<TWTable striped dense hoverStyle="clickable">
  <TWTableHead>
    <TWTableRow>
      <TWTableHeader>Name</TWTableHeader>
    </TWTableRow>
  </TWTableHead>
  <TWTableBody>
    <TWTableRow onClick={() => navigate('/details/1')}>
      <TWTableCell>Eintrag 1</TWTableCell>
    </TWTableRow>
  </TWTableBody>
</TWTable>
*/
